import { Heart, Users, TrendingUp, Gift } from "lucide-react";

const steps = [
  {
    number: "1",
    icon: Heart,
    title: "Create a moment-savings",
    subtitle: "Birthday, first steps, Christmas...",
    description: "Choose a milestone in your child's life and create a dedicated savings fund for that moment",
    details: ["Unique shareable link", "Personalized memory page", "Financial + emotional tracking"],
  },
  {
    number: "2",
    icon: Users,
    title: "Share with loved ones",
    subtitle: "Grandparents, godparents, friends...",
    description: "Loved ones contribute to the savings and add photos, videos, and heartfelt messages",
    details: ["Simplified contributions", "Photo/video memories", "Personalized wishes"],
  },
  {
    number: "3",
    icon: TrendingUp,
    title: "Automatic investment + memories",
    subtitle: "Dual growth: emotional + financial",
    description: "The memory album becomes a life journal. Meanwhile, money is invested and grows automatically",
    details: ["Emotional time capsule", "Automatic diversified investment", "Dual legacy: memories + capital"],
  },
];

const NewHowItWorksSectionEn = () => {
  return (
    <section id="how-it-works" className="section-padding bg-gradient-to-b from-pink-50/50 via-purple-50/30 to-white">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block bg-legatia-green/20 text-legatia-green rounded-full px-6 py-2 mb-4">
            <span className="text-sm font-semibold">One Legatia account → Moment-savings → Capital at 18</span>
          </div>
          <h2 className="section-title">How does it work?</h2>
          <p className="subtitle-hero max-w-3xl mx-auto">
            3 simple steps to turn moments into memories and capital
          </p>
        </div>

        <div className="max-w-5xl mx-auto space-y-12">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`
                flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} 
                gap-8 items-center
              `}
            >
              {/* Icon + Number */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center shadow-xl">
                    <step.icon className="h-12 w-12 md:h-16 md:w-16 text-white" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-white rounded-full border-4 border-legatia-green flex items-center justify-center shadow-lg">
                    <span className="text-lg font-bold text-legatia-green">{step.number}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 space-y-4">
                <div>
                  <div className="inline-block bg-legatia-green/10 text-legatia-green px-3 py-1 rounded-full text-xs font-semibold mb-2">
                    {step.subtitle}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-2">{step.title}</h3>
                  <p className="text-lg text-muted-foreground">{step.description}</p>
                </div>

                <div className="grid sm:grid-cols-3 gap-3">
                  {step.details.map((detail, idx) => (
                    <div 
                      key={idx}
                      className="bg-card border border-legatia-green/20 rounded-lg p-3 text-sm font-medium text-center hover:shadow-md transition-shadow"
                    >
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-legatia-green/10 to-future/10 border-2 border-legatia-green/30 rounded-2xl p-6">
            <Gift className="h-8 w-8 text-legatia-green mx-auto mb-3" />
            <p className="text-lg font-semibold text-foreground mb-2">
              Result: A memory album + growing capital for each milestone
            </p>
            <p className="text-sm text-muted-foreground">
              Your child will receive both priceless memories and financial support at age 18
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewHowItWorksSectionEn;
